import React, { useState } from 'react';
import Card from '~/src/components/Card';
import Button from '~/src/components/Button';
import QuantityControl from '~/src/components/QuantityControl/QuantityControl';
import InlineNotification from '~/src/components/Notification/InlineNotification';
import ModalOverlay from '~/src/components/Modal/Overlay';
import cbsSubscriptionsService from '~/src/services/api/cbsSubscriptionsService';
import useUser from '~/src/hooks/useUser';
import { CourtFormsPreviewModal } from './CourtFormsPreviewModal';

interface CourtFormsAddOnCardProps {
  subscription: any;
}

export const CourtFormsAddOnCard = ({
  subscription,
}: CourtFormsAddOnCardProps) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newTotal, setNewTotal] = useState(
    subscription?.contentPackagesPurchased || 1,
  );
  const [previewData, setPreviewData] = useState<any>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const { currentOrganization } = useUser();

  const currentLibraries = subscription?.contentPackagesPurchased || 0;
  const hasChanges = newTotal !== currentLibraries;

  const handleIncrement = () => {
    setNewTotal((prev: number) => prev + 1);
  };

  const handleDecrement = () => {
    if (newTotal > 1) {
      setNewTotal((prev: number) => prev - 1);
    }
  };

  const handlePreview = async () => {
    if (!currentOrganization?.fprint) {
      setError('Organization information not available');
      return;
    }

    setLoading(true);
    setError(null);
    setMessage(null);
    setPreviewData(null);

    try {
      const queryParams = {
        purchase_court_forms_addon: newTotal.toString(),
      };

      const result = await cbsSubscriptionsService.getSubscriptionPreview(
        currentOrganization.fprint,
        queryParams,
      );

      setPreviewData(result);
      setShowPreviewModal(true);
    } catch (err: any) {
      // Handle all 400 errors generically
      if (err.status === 400) {
        setError(
          'Unable to process the requested change. Please try again or contact support.',
        );
      } else {
        setError('Failed to load preview. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmUpdate = async () => {
    if (!currentOrganization?.fprint) {
      setError('Organization information not available');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setMessage(null);

    try {
      const updateData = {
        purchase_court_forms_addon: newTotal,
      };

      await cbsSubscriptionsService.updateSubscription(
        currentOrganization.fprint,
        updateData,
      );

      setMessage(
        `Successfully updated to ${newTotal} Court Forms ${
          newTotal === 1 ? 'library' : 'libraries'
        }!`,
      );

      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to update Court Forms libraries');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelPreview = () => {
    setShowPreviewModal(false);
    setPreviewData(null);
  };

  // Only show for eligible plans (Court Forms and Bundle plans)
  const isEligiblePlan = subscription?.isEligibleForCourtFormsAddon || false;
  const addonCreated = subscription?.contentPackagesAddonCreated || false;

  if (!isEligiblePlan || !addonCreated) {
    return null;
  }

  return (
    <>
      <Card>
        <div className="flex items-center justify-between">
          <div className="text-lg font-bold">Change Court Forms</div>
          <QuantityControl
            value={newTotal}
            onIncrement={handleIncrement}
            onDecrement={handleDecrement}
            disabled={loading}
            minValue={1}
          />
        </div>

        <div className="space-y-4 mt-0.5">
          {newTotal < currentLibraries && (
            <div className="mt-2">
              <InlineNotification
                type="warning"
                title="Note"
                message="Decreases take effect at your next billing cycle."
              />
            </div>
          )}

          {hasChanges && (
            <div className="pt-2">
              <Button
                onClick={handlePreview}
                disabled={loading}
                primary
                className="w-full py-2 text-sm"
              >
                {loading
                  ? 'Loading Preview...'
                  : `Preview Update to ${newTotal} ${
                      newTotal === 1 ? 'library' : 'libraries'
                    }`}
              </Button>
            </div>
          )}

          {message && (
            <InlineNotification
              type="success"
              title="Success"
              message={message}
            />
          )}

          {error && (
            <InlineNotification
              type="error"
              title="Update Failed"
              message={error}
            />
          )}
        </div>
      </Card>

      <ModalOverlay show={showPreviewModal} onCancel={handleCancelPreview}>
        <CourtFormsPreviewModal
          previewData={previewData}
          currentLibraries={currentLibraries}
          newTotal={newTotal}
          isProcessing={isProcessing}
          onCancel={handleCancelPreview}
          onConfirm={handleConfirmUpdate}
        />
      </ModalOverlay>
    </>
  );
};
